import type { RoleplayOrgScenarioRow, RoleplayScenarioListItem } from "@/lib/training/trainingApiClient";

export type RoleplayTrainingCatalogTile =
  | {
      kind: "org";
      rowIndex: number;
      scenarioKey: string;
      label: string;
      isActive: boolean;
      catalogHint: string;
    }
  | {
      kind: "readonly";
      scenarioKey: string;
      label: string;
      source: "manufacturer" | "platform_example";
    };

function sourceHint(source: RoleplayScenarioListItem["source"]): string {
  if (source === "platform_example") return "platform example";
  if (source === "manufacturer") return "manufacturer";
  return "org";
}

/** Trainee-merge order plus org-only drafts not present in the merged list. */
export function buildRoleplayTrainingCatalogTiles(
  rows: RoleplayOrgScenarioRow[],
  mergedPreview: RoleplayScenarioListItem[],
): RoleplayTrainingCatalogTile[] {
  const mergedKeys = new Set(mergedPreview.map((m) => m.key));
  const tiles: RoleplayTrainingCatalogTile[] = [];

  for (const m of mergedPreview) {
    const rowIndex = rows.findIndex((r) => r.scenario_key === m.key);
    if (rowIndex >= 0) {
      const r = rows[rowIndex]!;
      tiles.push({
        kind: "org",
        rowIndex,
        scenarioKey: m.key,
        label: r.label?.trim() || m.label || m.key,
        isActive: r.is_active,
        catalogHint: sourceHint(m.source),
      });
    } else if (m.source === "manufacturer" || m.source === "platform_example") {
      tiles.push({
        kind: "readonly",
        scenarioKey: m.key,
        label: m.label || m.key,
        source: m.source,
      });
    }
  }

  const orphanIndices = rows
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => !mergedKeys.has(r.scenario_key))
    .sort((a, b) => (a.r.sort_order ?? 0) - (b.r.sort_order ?? 0) || (a.r.label || "").localeCompare(b.r.label || ""));

  for (const { r, i } of orphanIndices) {
    tiles.push({
      kind: "org",
      rowIndex: i,
      scenarioKey: r.scenario_key,
      label: r.label?.trim() || r.scenario_key,
      isActive: r.is_active,
      catalogHint: "org (not in merge yet)",
    });
  }

  return tiles;
}
